import React, { useState } from 'react';

import { usePermission } from 'hooks';
import { IEvent, IUserGroupCandidate } from 'types';
import UserSearch from './UserSearch';
import EnrollSearch from './EnrollSearch';
import GroupSearch from './GroupSearch';
import SelectedList from './SelectedList';
import * as PickerStyle from './style';

export type PickerSourceType = 'USER' | 'GROUP' | 'ENROLL';

const SOURCE_LABEL: Record<PickerSourceType, string> = {
  USER: '회원 검색',
  GROUP: '회원 그룹',
  ENROLL: '봉사 기준',
};

/** 탭별 필요 권한. 권한이 없으면 탭을 숨긴다. */
const SOURCE_PERMISSION: Partial<Record<PickerSourceType, string>> = {
  USER: 'user:list',
  GROUP: 'user_group:list',
};

interface Props {
  selected: IUserGroupCandidate[];
  onChange: (selected: IUserGroupCandidate[]) => void;
  sources?: PickerSourceType[];
  /** 봉사 기준 탭에서 처음부터 골라둘 봉사 */
  initialEvent?: IEvent;
  /** true 면 initialEvent 에서 다른 봉사로 바꿀 수 없다 */
  isEventLocked?: boolean;
}

/** 회원 검색·회원 그룹·봉사 참여자 조회로 대상 회원을 고른다. */
function Picker({
  selected,
  onChange,
  sources = ['USER', 'ENROLL'],
  initialEvent,
  isEventLocked = false,
}: Props) {
  const can = usePermission();
  const tabs = sources.filter((value) => {
    const permission = SOURCE_PERMISSION[value];
    return !permission || can(permission);
  });
  const [source, setSource] = useState<PickerSourceType>(tabs[0] ?? 'ENROLL');
  const selectedIds = new Set(selected.map((u) => u.id));

  const handleAdd = (users: IUserGroupCandidate[]) => {
    const next = [...selected];
    const ids = new Set(selectedIds);
    users.forEach((u) => {
      if (ids.has(u.id)) return;
      ids.add(u.id);
      next.push(u);
    });
    onChange(next);
  };

  const handleRemove = (id: number) =>
    onChange(selected.filter((u) => u.id !== id));

  return (
    <div css={PickerStyle.columns}>
      <div css={PickerStyle.sourceColumn}>
        <div css={PickerStyle.tabs}>
          {tabs.map((value) => (
            <button
              key={value}
              type="button"
              css={PickerStyle.tab(source === value)}
              onClick={() => setSource(value)}
            >
              {SOURCE_LABEL[value]}
            </button>
          ))}
        </div>
        {/* 탭을 오가도 고른 그룹·봉사가 유지되도록 숨기기만 한다 */}
        {tabs.map((value) => (
          <div key={value} css={PickerStyle.panel(source === value)}>
            {value === 'USER' && (
              <UserSearch selectedIds={selectedIds} onAdd={handleAdd} />
            )}
            {value === 'GROUP' && (
              <GroupSearch selectedIds={selectedIds} onAdd={handleAdd} />
            )}
            {value === 'ENROLL' && (
              <EnrollSearch
                selectedIds={selectedIds}
                onAdd={handleAdd}
                initialEvent={initialEvent}
                isEventLocked={isEventLocked && !!initialEvent}
              />
            )}
          </div>
        ))}
      </div>
      <SelectedList
        selected={selected}
        onRemove={handleRemove}
        onClear={() => onChange([])}
      />
    </div>
  );
}

export default Picker;
